import { useEffect } from 'react';
import { create } from 'zustand';
import api from '@/lib/api';
import type { TeamCatalogMember, TeamMember } from '@/types';

/**
 * De teamlijst waaruit teampagina's kiezen. Eén keer per bezoek opgehaald via
 * het publieke endpoint, zodat editor, klantlink en PDF dezelfde leden zien.
 * Gearchiveerde leden blijven erin: pagina's waar ze al op staan tonen ze nog.
 */
interface TeamMemberState {
  members: TeamCatalogMember[];
  byId: Record<string, TeamCatalogMember>;
  status: 'idle' | 'loading' | 'ready' | 'error';
  load: () => Promise<void>;
  add: (input: { name: string; title: string; photoUrl: string }) => Promise<TeamCatalogMember>;
  update: (
    id: string,
    input: { name: string; title: string; photoUrl: string }
  ) => Promise<TeamCatalogMember>;
  setArchived: (id: string, archived: boolean) => Promise<void>;
}

export const useTeamMemberStore = create<TeamMemberState>()((set, get) => {
  const setMembers = (members: TeamCatalogMember[]) =>
    set({ members, byId: Object.fromEntries(members.map((member) => [member.id, member])) });
  return {
    members: [],
    byId: {},
    status: 'idle',
    load: async () => {
      const { status } = get();
      if (status === 'loading' || status === 'ready') return;
      set({ status: 'loading' });
      try {
        setMembers(await api.getTeamMembers());
        set({ status: 'ready' });
      } catch {
        set({ status: 'error' });
      }
    },
    add: async (input) => {
      const member = await api.createTeamMember(input);
      setMembers([...get().members, member]);
      return member;
    },
    update: async (id, input) => {
      const member = await api.updateTeamMember(id, input);
      setMembers(get().members.map((current) => (current.id === id ? member : current)));
      return member;
    },
    setArchived: async (id, archived) => {
      const member = await (archived ? api.archiveTeamMember(id) : api.restoreTeamMember(id));
      setMembers(get().members.map((current) => (current.id === id ? member : current)));
    },
  };
});

/** Teamlijst voor een component; haalt hem op bij het eerste gebruik. */
export function useTeamMembers() {
  const state = useTeamMemberStore();
  useEffect(() => {
    void useTeamMemberStore.getState().load();
  }, []);
  return state;
}

/** Verouderde ids in opgeslagen offertes → huidige id (bijv. ex-teamleden). */
const teamMemberIdAliases: Record<string, string> = {
  sjoerd: 'rick',
};

export function resolveTeamMemberId(id: string): string {
  return teamMemberIdAliases[id] ?? id;
}

/** Teamlijst eerst; anders een rij uit de blok-content (een template kan eigen leden meegeven). */
export function resolveTeamMember(
  id: string,
  membersFromContent: TeamMember[] | undefined,
  catalog: Record<string, TeamCatalogMember>
): { name: string; firstName: string; title: string; img: string; archived: boolean } | null {
  const fromCatalog = catalog[resolveTeamMemberId(id)];
  const row = fromCatalog
    ? { name: fromCatalog.name, role: fromCatalog.title, photoUrl: fromCatalog.photoUrl }
    : membersFromContent?.find((member) => member.id === id);
  if (!row?.photoUrl) return null;
  return {
    name: row.name,
    firstName: row.name.trim().split(/\s+/)[0] ?? row.name,
    title: row.role ?? '',
    img: row.photoUrl,
    archived: fromCatalog?.archived ?? false,
  };
}
